import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '@/assets/Icons/SearchIcon/SearchIcon';
import { CloseIcon } from '@/assets/Icons/CloseIcon/CloseIcon';
import { useSearchSuggest } from '@/shared/hooks/useSearchSuggest/useSearchSuggest';
import { useDebouncedSearch } from '@/shared/hooks/useDebouncedSearch/useDebouncedSearch';
import { resolveImageSrc } from '@/shared/utils/getAssetUrl';
import type { SearchSuggestion } from '@/shared/types/Search';

interface SearchAutocompleteProps {
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  onNavigate?: () => void;
}

const TYPE_CONFIG: Record<string, { bg: string; text: string; label: string; path: string }> = {
  brand: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Brands', path: '/brands' },
  ingredient: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    label: 'Ingredients',
    path: '/ingredients',
  },
  category: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    label: 'Categories',
    path: '/health-categories',
  },
  supplier: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    label: 'Suppliers',
    path: '/suppliers',
  },
  article: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    label: 'News',
    path: '/news',
  },
  event: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    label: 'Events',
    path: '/events',
  },
};

const SUGGEST_TYPES = ['brand', 'ingredient', 'category', 'supplier', 'article', 'event'] as const;

function SuggestionThumbnail({ suggestion }: { suggestion: SearchSuggestion }) {
  const colors = TYPE_CONFIG[suggestion.type] ?? TYPE_CONFIG.brand;
  const initial = suggestion.text.charAt(0).toUpperCase();

  if (suggestion.image_url) {
    return (
      <div className='h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-50'>
        <img
          src={resolveImageSrc(suggestion.image_url, 'search')}
          alt=''
          className='h-full w-full object-contain'
          loading='lazy'
        />
      </div>
    );
  }

  return (
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${colors.bg}`}>
      <span className={`text-base font-semibold ${colors.text}`}>{initial}</span>
    </div>
  );
}

export function SearchAutocomplete({
  className = '',
  inputClassName = '',
  placeholder = 'Search brands, ingredients, categories...',
  onNavigate,
}: SearchAutocompleteProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    searchValue,
    searchQuery,
    onSearchChange: rawOnSearchChange,
  } = useDebouncedSearch({
    delay: 300,
    minLength: 2,
  });

  const { suggestions, isLoading } = useSearchSuggest(searchQuery);

  const hasQuery = searchQuery.trim().length >= 2;
  const isOpen = hasQuery && !dismissed;
  const totalItems = suggestions.length + (hasQuery ? 1 : 0);

  const grouped = useMemo(() => {
    const result: Record<string, SearchSuggestion[]> = {};
    for (const t of SUGGEST_TYPES) {
      result[t] = suggestions.filter(s => s.type === t);
    }
    return result;
  }, [suggestions]);

  const flatOrder = useMemo(() => {
    const order: SearchSuggestion[] = [];
    const maxLen = Math.max(...SUGGEST_TYPES.map(t => grouped[t].length));
    for (let i = 0; i < maxLen; i++) {
      for (const t of SUGGEST_TYPES) {
        if (i < grouped[t].length) order.push(grouped[t][i]);
      }
    }
    return order;
  }, [grouped]);

  const onSearchChange = useCallback(
    (value: string) => {
      rawOnSearchChange(value);
      setDismissed(false);
      setHighlightIndex(-1);
    },
    [rawOnSearchChange]
  );

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const navigateToSearch = useCallback(
    (q: string) => {
      if (q.trim().length < 2) return;
      dismiss();
      rawOnSearchChange('');
      onNavigate?.();
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    },
    [navigate, rawOnSearchChange, dismiss, onNavigate]
  );

  const navigateToItem = useCallback(
    (slug: string, type: string) => {
      dismiss();
      rawOnSearchChange('');
      onNavigate?.();
      const config = TYPE_CONFIG[type];
      const path = config ? `${config.path}/${slug}` : `/brands/${slug}`;
      navigate(path);
    },
    [navigate, rawOnSearchChange, dismiss, onNavigate]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDismissed(true);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' && searchValue.trim().length >= 2) {
        navigateToSearch(searchValue);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
          const item = flatOrder[highlightIndex];
          if (item) navigateToItem(item.slug, item.type);
        } else {
          navigateToSearch(searchValue);
        }
        break;
      case 'Escape':
        dismiss();
        inputRef.current?.blur();
        break;
    }
  };

  const handleClear = () => {
    rawOnSearchChange('');
    setDismissed(true);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (hasQuery) {
      setDismissed(false);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    setIsFocused(false);
  };

  const getFlatIndex = (suggestion: SearchSuggestion) => flatOrder.indexOf(suggestion);

  // Only render columns that have results or whose type had suggestions in a neighbor
  const activeTypes = SUGGEST_TYPES.filter(t => grouped[t].length > 0);

  const renderColumn = (items: SearchSuggestion[], type: string) => {
    const config = TYPE_CONFIG[type] ?? TYPE_CONFIG.brand;
    return (
      <div className='min-w-0 flex-1'>
        <p className='mb-1.5 px-1 text-[11px] font-semibold tracking-wide text-gray-400 uppercase'>
          {config.label}
        </p>
        {items.length === 0 ? (
          <p className='px-1 py-2 text-xs text-gray-400'>No {config.label.toLowerCase()} found</p>
        ) : (
          <div className='space-y-0.5'>
            {items.map(suggestion => {
              const flatIdx = getFlatIndex(suggestion);
              return (
                <button
                  key={`${suggestion.type}-${suggestion.slug}`}
                  type='button'
                  className={`flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition-colors ${
                    highlightIndex === flatIdx ? 'bg-sky-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => navigateToItem(suggestion.slug, suggestion.type)}
                  onMouseEnter={() => setHighlightIndex(flatIdx)}
                  role='option'
                  aria-selected={highlightIndex === flatIdx}
                >
                  <SuggestionThumbnail suggestion={suggestion} />
                  <p
                    className={`min-w-0 truncate text-sm font-medium ${
                      highlightIndex === flatIdx ? 'text-sky-700' : 'text-gray-900'
                    }`}
                  >
                    {suggestion.text}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`relative transition-all duration-300 ease-in-out ${className} ${isFocused ? 'max-w-2xl!' : ''}`}
    >
      <div className='relative'>
        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5'>
          <SearchIcon size={18} className='h-4.5 w-4.5 text-gray-400' />
        </div>
        <input
          ref={inputRef}
          type='text'
          value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`block w-full rounded-lg border-0 bg-white py-2.5 pr-9 pl-10 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:ring-2 focus:ring-white focus:outline-none ${inputClassName}`}
          placeholder={placeholder}
          aria-label='Search'
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          role='combobox'
          autoComplete='off'
        />
        {searchValue && (
          <button
            type='button'
            onClick={handleClear}
            className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600'
            aria-label='Clear search'
          >
            <CloseIcon size={16} />
          </button>
        )}
      </div>

      {isOpen && (
        <div
          className='right-0 z-50 mt-2 bg-white shadow-2xl ring-1 ring-black/10 max-md:relative max-md:max-h-[60vh] max-md:w-full max-md:overflow-y-auto max-md:rounded-lg md:absolute md:top-full md:w-[min(800px,92vw)] md:rounded-xl'
          role='listbox'
        >
          {isLoading && suggestions.length === 0 ? (
            <div className='flex items-center justify-center px-6 py-10'>
              <div className='border-t-primary h-5 w-5 animate-spin rounded-full border-2 border-gray-200' />
              <span className='ml-3 text-sm text-gray-500'>Searching...</span>
            </div>
          ) : (
            <>
              {suggestions.length === 0 && !isLoading ? (
                <div className='px-6 py-10 text-center text-sm text-gray-500'>
                  No suggestions found for &ldquo;{searchValue}&rdquo;
                </div>
              ) : (
                <div
                  className={`grid grid-cols-2 gap-0 divide-x divide-gray-100 p-4 ${
                    activeTypes.length <= 3
                      ? { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' }[
                          activeTypes.length
                        ]
                      : 'lg:grid-cols-6'
                  }`}
                >
                  {activeTypes.length <= 3
                    ? activeTypes.map(t => (
                        <div key={t} className='px-2 first:pl-0 last:pr-0'>
                          {renderColumn(grouped[t], t)}
                        </div>
                      ))
                    : SUGGEST_TYPES.map(t => (
                        <div key={t} className='px-2 first:pl-0 last:pr-0'>
                          {renderColumn(grouped[t], t)}
                        </div>
                      ))}
                </div>
              )}

              {hasQuery && (
                <div className='border-t border-gray-100'>
                  <button
                    type='button'
                    className={`flex w-full items-center gap-3 rounded-b-lg px-4 py-3 text-left transition-colors md:rounded-b-xl ${
                      highlightIndex === suggestions.length ? 'bg-sky-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => navigateToSearch(searchValue)}
                    onMouseEnter={() => setHighlightIndex(suggestions.length)}
                    role='option'
                    aria-selected={highlightIndex === suggestions.length}
                  >
                    <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50'>
                      <SearchIcon size={18} className='text-primary' />
                    </div>
                    <span className='text-sm text-gray-600'>
                      View all results for &ldquo;
                      <span className='font-semibold text-gray-900'>{searchValue}</span>
                      &rdquo;
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
